import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Users, ArrowLeft, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const RANGES = [14, 28, 42];
const today = () => new Date().toISOString().slice(0, 10);
const SHIFTS = ["morning", "afternoon", "evening", "night"];

export default function AdminDriverAvailability() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [start, setStart] = useState(today());
  const [days, setDays] = useState(28);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      setData((await api.get("/admin/driver-availability", { params: { start, days } })).data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [start, days, isAdmin]);

  useEffect(() => { load(); }, [load]);

  if (!isAdmin)
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <p className="mx-auto max-w-3xl px-5 py-24 text-slate-600" data-testid="dav-no-access">{t("admin.noAccess")}</p>
      </div>
    );

  const dot = (d) => (d.available === 0 ? "bg-rose-500" : d.off > 0 ? "bg-amber-500" : "bg-emerald-500");

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-7xl px-5 py-12 lg:px-10">
        <Link to="/admin" data-testid="dav-back-btn" className="inline-flex items-center gap-2 text-sm text-slate-500 transition-colors duration-200 hover:text-sea">
          <ArrowLeft size={15} /> {t("admin.title")}
        </Link>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="label-eyebrow">{t("admin.title")}</p>
            <h1 className="mt-2 flex items-center gap-2 font-display text-3xl tracking-tight sm:text-4xl" data-testid="dav-title">
              <Users size={22} className="text-sea" /> {t("sch.driverCalendar")}
            </h1>
            {data && <p className="mt-2 text-sm text-slate-600">{t("sch.total")}: <b>{data.total_drivers}</b></p>}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Input type="date" data-testid="dav-start-input" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" />
            <select data-testid="dav-range-select" value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
              {RANGES.map((r) => <option key={r} value={r}>{r} {t("fc.days")}</option>)}
            </select>
            <Button variant="outline" data-testid="dav-refresh-btn" onClick={load}>
              <RefreshCw size={15} className="mr-1.5" /> {t("common.refresh")}
            </Button>
            <Link to="/admin/calendar" data-testid="dav-to-fleet-link">
              <Button className="bg-sea hover:bg-sea-dark"><CalendarDays size={15} className="mr-1.5" /> {t("fc.calendar")}</Button>
            </Link>
          </div>
        </div>

        {loading && !data && <p className="mt-10 text-slate-500" data-testid="dav-loading">{t("common.loading")}</p>}

        {data && (
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7" data-testid="dav-grid">
            {data.calendar.map((d) => (
              <div key={d.date} data-testid={`dav-day-${d.date}`} className="rounded-xl border border-slate-200 bg-white p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-900">{d.date.slice(5)}</span>
                  <span className={`h-2.5 w-2.5 rounded-full ${dot(d)}`} />
                </div>
                <p className="mt-0.5 text-[11px] text-slate-400">{d.weekday.slice(0, 3)}</p>
                <p className="mt-2 text-sm font-medium text-slate-900">
                  {d.available}/{d.total} <span className="text-[11px] font-normal text-slate-500">{t("sch.available")}</span>
                </p>
                {d.off > 0 && <p className="text-[11px] font-medium text-amber-600">{d.off} {t("sch.off")}</p>}
                <p className="text-[11px] text-slate-500">{t("sch.needed")}: {d.needed}</p>
                <div className="mt-2 grid grid-cols-4 gap-1" title={t("sch.shifts")}>
                  {SHIFTS.map((s) => {
                    const v = d.by_shift?.[s] ?? 0;
                    const ok = v >= 1;
                    return <span key={s} className={`h-1.5 rounded-full ${ok ? "bg-sea" : "bg-slate-200"}`} title={`${t(`sch.${s}`)}: ${v}`} />;
                  })}
                </div>
                {d.drivers?.length > 0 && <p className="mt-1 text-xs">{d.drivers.map((x) => x.emoji).join(" ")}</p>}
                {d.off_names?.length > 0 && <p className="mt-1 truncate text-[11px] text-slate-400" title={d.off_names.join(", ")}>{d.off_names.join(", ")}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
